import { useEffect, useState } from "react";
import { useAppDispatch } from "../redux/hooks";
import { getSessionId, getTableNumber, updatePhoto, updateCreateResult, getName } from "../redux/slices";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loading } from "./Loading";
import { trackEvent } from "../utils/analytics";
import { PlayerPhotoSelect } from "./playerEntryPhotoSelect";
import { PlayerPhotoCapture } from "./playerEntryPhotoCapture";
import { PlayerAvatarSelect } from "./playerEntryAvatarSelect";
import { CreatePlayerDto, PlayerCreateResponse } from "../types/player";
import { setPlayerCookie } from "../utils/cookies";
import "./playerEntryPhoto.scss";

export const PlayerPhoto: React.FC = () => {
  const dispatch = useAppDispatch();
  const nav = useNavigate();

  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [showAvatarSelect, setShowAvatarSelect] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // BEGIN copied link checking

  const [loading, setLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();

  // if the redux store is missing a session ID (or the session ID does not match the URL parameter),
  // then redirect to the start page. Suspect that this is a shared link and this "new" user needs to start at the beginning of the journey
  let sessionId = searchParams.get("s");
  let tableNumber = searchParams.get("t");
  const retake = searchParams.get("retake") === "true";
  const goToAvatar = searchParams.get("avatar") === "true";
  let venueCode = searchParams.get("v");
  const sessionIdRedux = useSelector(getSessionId);
  const tableNumberRedux = useSelector(getTableNumber);
  const name = useSelector(getName);

  useEffect(() => {
    if (sessionIdRedux === null || tableNumberRedux === null) {
      nav(`/?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    }

    if (sessionId !== sessionIdRedux || tableNumber !== tableNumberRedux) {
      nav(`/?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    } else {
      if (retake) {
        setShowCamera(true);
      }
      if (goToAvatar) {
        setShowAvatarSelect(true);
      }
      if (sessionStorage.getItem("hasVisitedPhotoPage") === null) {
        trackEvent({
          gaEvent: "photo_page_opened",
          params: { venue_code: venueCode },
        });
        sessionStorage.setItem("hasVisitedPhotoPage", "true");
      }
      setLoading(false);
    }
  }, [sessionId, tableNumber, venueCode, sessionIdRedux, tableNumberRedux, nav, retake, goToAvatar]);

  // END of "copied link" checking

  if (loading || submitting) return <Loading />;

  const onCameraError = () => {
    trackEvent({
      gaEvent: "camera_error",
      clarityEvent: "camera_error",
      params: { venue_code: venueCode },
    });
    nav(`/cameraerror?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  const onCapture = (imageSrc: string) => {
    dispatch(updatePhoto(imageSrc));
    if (sessionStorage.getItem("hasTakenPhoto") === null) {
      trackEvent({
        gaEvent: "photo_taken",
        clarityEvent: "photo_taken",
        params: { venue_code: venueCode },
      });
      sessionStorage.setItem("hasTakenPhoto", "true");
    }
    nav(`/confirm?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  const onBack = () => {
    nav(`/nickname?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  const onAvatarChosen = async (avatarSrc: string) => {
    setShowAvatarSelect(false);
    setSubmitting(true);

    // convert to JPEG via canvas — the game expects the same format as the webcam capture
    const base64Src = await new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("canvas unavailable"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = avatarSrc;
    });

    dispatch(updatePhoto(base64Src));
    const playerDTO: CreatePlayerDto = { Name: name, Photo: base64Src };
    const region = process.env.REACT_APP_REGION;
    try {
      const response = await fetch(
        `/api/v1/${region}/electricshuffle/sessions/${sessionId}/players?t=${tableNumber}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(playerDTO),
        },
      );
      if (response.ok) {
        const createResult: PlayerCreateResponse = await response.json();
        dispatch(updateCreateResult(createResult));
        setPlayerCookie(createResult.playerUid);
        trackEvent({
          gaEvent: "PlayerEntered",
          clarityEvent: "player_joined_game",
          params: { venue_code: venueCode },
        });
      } else {
        dispatch(
          updateCreateResult({
            createSuccess: false,
            eventType: 0,
            playspaceNumber: 0,
            playerUid: "",
            reason: "Something went wrong",
            teamName: "",
          }),
        );
      }
    } catch {
      dispatch(
        updateCreateResult({
          createSuccess: false,
          eventType: 0,
          playspaceNumber: 0,
          playerUid: "",
          reason: "Something went wrong",
          teamName: "",
        }),
      );
    }

    nav(`/details?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  if (showCamera) {
    return (
      <PlayerPhotoCapture
        onCapture={onCapture}
        onBack={() => setShowCamera(false)}
        onCameraError={onCameraError}
      />
    );
  }

  if (showAvatarSelect) {
    return <PlayerAvatarSelect onAvatarChosen={onAvatarChosen} onBack={() => setShowAvatarSelect(false)} />;
  }

  return (
    <PlayerPhotoSelect
      name={name ?? ""}
      onTakePhoto={() => setShowCamera(true)}
      onChooseAvatar={() => {
        trackEvent({ gaEvent: "avatar_start" });
        setShowAvatarSelect(true);
      }}
      onBack={onBack}
    />
  );
};
